import React, { useState, useRef } from 'react';

const PromptVersionsBadge = ({
  promptsUsed,
  currentPromptVersions,
  labels = {},
  order = [],
  contextNoun = 'record',
  align = 'right',
}) => {
  const [visible, setVisible] = useState(false);
  const timeoutRef = useRef(null);

  if (!promptsUsed || Object.keys(promptsUsed).length === 0) return null;

  const keys = order.length > 0 ? order : Object.keys(promptsUsed);
  const entries = keys
    .filter((key) => promptsUsed[key])
    .map((key) => {
      const used = promptsUsed[key].version;
      const current = currentPromptVersions?.[key]?.version;
      return {
        key,
        label: labels[key] || key,
        used,
        current,
        isOutdated: current != null && used !== current,
      };
    });

  if (entries.length === 0) return null;

  const hasOutdated = entries.some((e) => e.isOutdated);

  const handleEnter = () => {
    clearTimeout(timeoutRef.current);
    setVisible(true);
  };

  const handleLeave = () => {
    timeoutRef.current = setTimeout(() => setVisible(false), 150);
  };

  const popoverSideClass = align === 'left' ? 'left-0' : 'right-0';

  return (
    <div className="relative inline-block" onMouseEnter={handleEnter} onMouseLeave={handleLeave}>
      <span
        className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold cursor-pointer ${
          hasOutdated ? 'bg-amber-100 text-amber-700' : 'bg-violet-100 text-violet-700'
        }`}
      >
        <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z"
          />
        </svg>
        Prompt Versions
      </span>

      {visible && (
        <div
          className={`absolute ${popoverSideClass} top-full mt-2 w-80 bg-white border border-gray-200 rounded-xl shadow-lg z-50 py-2.5 px-3`}
        >
          <div className="flex items-center justify-between mb-2">
            <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-400">Prompt Versions</p>
            <div className="flex gap-6 text-[10px] font-semibold uppercase tracking-wide text-gray-400">
              <span>Used</span>
              <span>Current</span>
            </div>
          </div>
          <div className="space-y-1">
            {entries.map(({ key, label, used, current, isOutdated }) => (
              <div key={key} className="flex items-center justify-between py-1">
                <span className="text-xs text-gray-600">{label}</span>
                <div className="flex items-center gap-3">
                  <span
                    className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold ${
                      isOutdated ? 'bg-amber-100 text-amber-700' : 'bg-violet-100 text-violet-700'
                    }`}
                  >
                    v{used}
                  </span>
                  {current != null ? (
                    <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold bg-green-100 text-green-700">
                      v{current}
                    </span>
                  ) : (
                    <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold bg-gray-100 text-gray-400">
                      —
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
          {hasOutdated && (
            <p className="text-[10px] text-amber-600 mt-2 pt-2 border-t border-gray-100">
              Prompts have been updated since this {contextNoun} was generated.
            </p>
          )}
        </div>
      )}
    </div>
  );
};

export default PromptVersionsBadge;
